/* hh-product-simple.js — controller for the simple-product template (accessories + art prints).

   - Adaptive variant selector: clicking a swatch/pill sets that option's value, resolves the
     matching variant, and updates the price, main image, and the Add-to-cart button (variant id +
     enabled/Sold-out/Unavailable state).
   - Gallery: thumbnail click swaps the main image.
   - Add to cart: posts the selected variant and opens the cart drawer (replicates the theme's
     renderContents + publish(cartUpdate) pattern — the product-page helpers aren't global).

   Reads two JSON blobs the section injects: [data-hh-sp-variants] (id/options/available/price/
   image per variant) and [data-hh-sp-strings] (button labels). */
(function () {
  'use strict';

  var root = document.querySelector('[data-hh-sp]');
  if (!root) return;

  function parseJSON(sel, fallback) {
    var el = root.querySelector(sel) || document.querySelector(sel);
    if (!el) return fallback;
    try { return JSON.parse(el.textContent); } catch (e) { return fallback; }
  }

  var variants = parseJSON('[data-hh-sp-variants]', []);
  var S = parseJSON('[data-hh-sp-strings]', { soldout: 'Sold out', addToCart: 'Add to cart', unavailable: 'Unavailable' });

  // ── Bag add-on (sets only; the block is absent everywhere else) ──
  // The bag is its own independent cart line — same instant "+$X" pattern as the Add On tab on
  // the body-care page. Its actual /cart/add.js call is wired by initAddonAtc() in hh-product.js
  // (data-hh-addon-atc, shared — nothing bag-specific needed for that part). This section only
  // has to keep the dropdown, the photo, and the add button's target variant in sync with each
  // other; it has no relationship to the set's own Add-to-cart button above.
  var addonEl = root.querySelector('[data-hh-sp-addon]');
  var addonToggle = addonEl && addonEl.querySelector('[data-hh-sp-addon-toggle]');
  var addonPanel = addonEl && addonEl.querySelector('[data-hh-sp-addon-panel]');
  var addonLabel = addonEl && addonEl.querySelector('[data-hh-sp-addon-select-label]');
  var addonSwatchPreview = addonEl && addonEl.querySelector('[data-hh-sp-addon-select-swatch]');
  var addonSwatchPreviewImg = addonSwatchPreview && addonSwatchPreview.querySelector('[data-hh-sp-addon-select-swatch-img]');

  function toggleAddonPanel(open) {
    if (!addonPanel || !addonToggle) return;
    addonPanel.hidden = !open;
    addonToggle.setAttribute('aria-expanded', open ? 'true' : 'false');
    addonToggle.classList.toggle('is-open', open);
  }

  // Picking a swatch: swaps the photo, fills the closed-dropdown label + swatch dot, closes the
  // panel, and unlocks Add (disabled until now — matches the reference: no variant is assumed).
  function selectAddonPreview(btn) {
    if (!addonEl || btn.hasAttribute('disabled')) return;

    var img = addonEl.querySelector('[data-hh-sp-addon-img]');
    var src = btn.getAttribute('data-image');
    var color = btn.getAttribute('data-color') || '';
    if (img && src && img.getAttribute('src') !== src) {
      img.setAttribute('src', src);
      img.setAttribute('alt', color);
    }

    addonEl.querySelectorAll('[data-hh-sp-addon-value]').forEach(function (b) {
      var on = b === btn;
      b.classList.toggle('is-selected', on);
      b.setAttribute('aria-selected', on ? 'true' : 'false');
    });

    if (addonLabel) addonLabel.textContent = color;
    if (addonSwatchPreview) {
      addonSwatchPreview.hidden = false;
      var swatchImg = btn.querySelector('img');
      if (addonSwatchPreviewImg && swatchImg) addonSwatchPreviewImg.src = swatchImg.src;
    }

    var atc = addonEl.querySelector('[data-hh-addon-atc]');
    if (atc) {
      atc.setAttribute('data-variant-id', btn.getAttribute('data-variant-id'));
      atc.removeAttribute('disabled');
      atc.removeAttribute('aria-disabled');
    }

    toggleAddonPanel(false);
  }

  // Outside click / Escape close the panel — same baseline behavior as every other dropdown/
  // overlay on the site (mega-menu, account drawer).
  document.addEventListener('click', function (e) {
    if (!addonPanel || addonPanel.hidden) return;
    if (!e.target.closest('[data-hh-sp-addon-select]')) toggleAddonPanel(false);
  });
  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape' && addonPanel && !addonPanel.hidden) toggleAddonPanel(false);
  });

  // selected[position] = value. Seed from the .is-selected buttons (or the first value).
  var optionEls = Array.prototype.slice.call(root.querySelectorAll('.hh-sp__option'));
  var selected = {};
  optionEls.forEach(function (o) {
    var pos = o.getAttribute('data-option-position');
    var sel = o.querySelector('[data-hh-sp-value].is-selected') || o.querySelector('[data-hh-sp-value]');
    if (sel) selected[pos] = sel.getAttribute('data-value');
  });

  function currentVariant() {
    var positions = Object.keys(selected);
    return variants.filter(function (v) {
      for (var i = 0; i < positions.length; i++) {
        var pos = parseInt(positions[i], 10);
        if (v.options[pos - 1] !== selected[positions[i]]) return false;
      }
      return true;
    })[0] || null;
  }

  var initialized = false;

  // Drive the shared product gallery (hh-product.js carousel) to a variant's image by clicking
  // its thumbnail — reuses the carousel's own goTo wiring. No-op on initial load (the carousel
  // self-positions at slide 0) and when the image isn't in the gallery.
  function showVariantImage(idx) {
    if (!initialized || idx == null || idx < 0) return;
    var thumb = document.querySelector('[data-hh-thumb="' + idx + '"]');
    if (thumb) thumb.click();
  }

  function update() {
    var v = currentVariant();
    var atc = root.querySelector('[data-hh-sp-atc]');
    var label = root.querySelector('[data-hh-sp-atc-label]');

    if (!v) {                                   // impossible combination
      atc.setAttribute('disabled', '');
      atc.removeAttribute('data-variant-id');
      if (label) label.textContent = S.unavailable;
    } else if (!v.available) {                   // exists but out of stock
      atc.setAttribute('disabled', '');
      atc.setAttribute('data-variant-id', v.id);
      if (label) label.textContent = S.soldout;
    } else {                                      // buyable — price lives in the button, like body care
      atc.removeAttribute('disabled');
      atc.removeAttribute('aria-disabled');
      atc.setAttribute('data-variant-id', v.id);
      if (label) {
        label.textContent = '';
        label.appendChild(document.createTextNode(S.addToCart + ' — '));
        // Plain set price, unaffected by the bag panel — the bag is its own cart line now, so
        // this button never needs to reflect what's chosen there.
        if (v.compareAtPrice) {
          var compareEl = document.createElement('s');
          compareEl.className = 'hh-sp__price-compare';
          compareEl.textContent = v.compareAtPrice;
          label.appendChild(compareEl);
          label.appendChild(document.createTextNode(' '));
          var saleEl = document.createElement('span');
          saleEl.className = 'hh-sp__price-sale';
          saleEl.textContent = v.price;
          label.appendChild(saleEl);
        } else {
          label.appendChild(document.createTextNode(v.price));
        }
      }
    }

    if (v) showVariantImage(v.imageIndex);       // jump the gallery to the variant's image (e.g. colour)

    optionEls.forEach(function (o) {
      var pos = o.getAttribute('data-option-position');
      var cur = o.querySelector('[data-hh-sp-current]');
      if (cur && selected[pos] != null) cur.textContent = selected[pos];
    });
  }

  // Pick a value; if that makes an impossible combo (sparse matrices like art prints'
  // Size×Medium), adopt the first in-stock variant carrying this value and snap the other
  // option(s) to match — so the customer never lands on an "Unavailable" dead-end.
  function selectValue(pos, value) {
    selected[pos] = value;
    if (!currentVariant()) {
      var matches = variants.filter(function (v) { return v.options[parseInt(pos, 10) - 1] === value; });
      var pick = matches.filter(function (v) { return v.available; })[0] || matches[0];
      if (pick) pick.options.forEach(function (val, i) { selected[String(i + 1)] = val; });
    }
    // re-sync every option group's selected highlight to the (possibly adjusted) selection
    optionEls.forEach(function (o) {
      var p = o.getAttribute('data-option-position');
      o.querySelectorAll('[data-hh-sp-value]').forEach(function (b) {
        b.classList.toggle('is-selected', b.getAttribute('data-value') === selected[p]);
      });
    });
    update();
  }

  /* ── Gift card recipient (snippets/hh-gift-card-recipient.liquid) ──
     Present only on a gift card product. Reveals the fields, counts the message down, and
     hands the ATC handler a line-item `properties` object.

     The control flag __shopify_send_gift_card_to_recipient is what makes Shopify email the
     RECIPIENT rather than the buyer. Without it every other field is inert decoration, so it
     is set here in one place alongside the values it governs — never conditionally. */
  var gc = root.querySelector('[data-hh-gc]');

  function gcEl(name) { return gc && gc.querySelector('[data-hh-gc-' + name + ']'); }

  function gcError(msg) {
    var box = gcEl('error');
    if (!box) return;
    box.textContent = msg || '';
    box.hidden = !msg;
  }

  function initGiftRecipient() {
    if (!gc) return;
    var toggle = gcEl('toggle'), fields = gcEl('fields'), msg = gcEl('message'),
        count = gcEl('count');

    toggle.addEventListener('change', function () {
      fields.hidden = !toggle.checked;
      if (!toggle.checked) {
        // Clear on collapse so a half-filled form can't ride along invisibly on the next add.
        [gcEl('email'), gcEl('name'), msg].forEach(function (f) { if (f) f.value = ''; });
        gcError('');
        if (count) count.textContent = '200';
      } else if (gcEl('email')) {
        gcEl('email').focus();
      }
    });

    if (msg && count) {
      msg.addEventListener('input', function () {
        count.textContent = String(200 - msg.value.length);
      });
    }
    // Typing anywhere in the panel clears a stale error rather than leaving it to contradict
    // what the customer has just corrected.
    fields.addEventListener('input', function () { gcError(''); });
  }

  /* Returns: null   → not a gift card, or "send as a gift" is off (add normally, buyer gets it)
              false  → gift mode on but invalid; message shown, caller MUST abort
              object → line item properties to attach                                        */
  function collectRecipient() {
    if (!gc) return null;
    var toggle = gcEl('toggle');
    if (!toggle || !toggle.checked) return null;

    var email = (gcEl('email').value || '').trim();
    if (!email) { gcError('Add the email address to send it to.'); gcEl('email').focus(); return false; }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      gcError("That email address doesn't look right."); gcEl('email').focus(); return false;
    }

    // NO "Send on" PROPERTY, DELIBERATELY (removed Aug 2026 — do not add it back casually).
    // Shopify reads any Send on value as a SCHEDULED send, delivered in a roughly 4-10am window,
    // so a card bought in the afternoon would not reach the recipient until the next morning.
    // Some browsers also paint today's date into an empty date input, so buyers who never touched
    // the field were still submitting one. Omitting the property is what makes Shopify issue the
    // card the moment the order is paid. __shopify_offset goes with it: its only job was to land
    // a scheduled send on the buyer's local day, and there is no schedule any more.
    var props = { '__shopify_send_gift_card_to_recipient': 'true', 'Recipient email': email };
    var name = (gcEl('name').value || '').trim();
    var note = (gcEl('message').value || '').trim();
    if (name) props['Recipient name'] = name;
    if (note) props['Message'] = note;
    return props;
  }

  /* Turn a /cart/add.js failure into one sentence a customer can act on.

     CAREFUL: on a recipient rejection Shopify's `description` is an OBJECT, not a string —
     verified live: {"status":422,"description":{"email":["is invalid"]},
     "message":"Validation failed: Email is invalid"}. Every other cart error (sold out, etc.)
     returns `description` as a string. Printing it blind renders "[object Object]", so the
     type is checked, and the one error this form can actually provoke gets its own wording —
     "Validation failed: Email is invalid" is a developer's sentence, not a shopper's. */
  function recipientErrorText(body) {
    var fallback = 'That didn’t go through. Check the email address and try again.';
    if (!body) return fallback;
    var fieldErrors = body.errors;
    if (fieldErrors && fieldErrors.email) return 'That email address was rejected. Check it and try again.';
    if (typeof body.description === 'string' && body.description) return body.description;
    if (typeof body.message === 'string' && body.message) return body.message;
    return fallback;
  }

  function postCart(items) {
    return fetch('/cart/add.js', {
      method: 'POST', headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ items: items, sections: ['cart-drawer', 'cart-icon-bubble'], sections_url: window.location.pathname })
    }).then(function (r) {
      // Reject explicitly on a 4xx rather than treating the error body as a cart state.
      if (!r.ok) return r.json().then(function (body) { throw { status: r.status, body: body }; });
      return r.json();
    });
  }

  function renderCart(state) {
    var cd = document.querySelector('cart-drawer');
    if (cd && typeof cd.renderContents === 'function') cd.renderContents(state);
    if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      publish(PUB_SUB_EVENTS.cartUpdate, { source: 'hh-sp', cartData: state });
    } else {
      document.dispatchEvent(new Event('shopify:cart:updated'));
    }
  }

  root.addEventListener('click', function (e) {
    // bag add-on dropdown toggle — open/close the swatch-grid panel
    if (e.target.closest('[data-hh-sp-addon-toggle]')) {
      toggleAddonPanel(!!addonPanel && addonPanel.hidden);
      return;
    }
    // bag add-on swatch — preview only, see selectAddonPreview
    var addonVal = e.target.closest('[data-hh-sp-addon-value]');
    if (addonVal) {
      if (!addonVal.hasAttribute('disabled')) selectAddonPreview(addonVal);
      return;
    }
    // option value (swatch or pill)
    var val = e.target.closest('[data-hh-sp-value]');
    if (val) {
      selectValue(val.getAttribute('data-position'), val.getAttribute('data-value'));
      return;
    }
    // add to cart — the set alone. The bag (data-hh-addon-atc) is a separate independent add,
    // wired by initAddonAtc() in hh-product.js — this handler has no knowledge of it.
    var atc = e.target.closest('[data-hh-sp-atc]');
    if (atc && !atc.hasAttribute('disabled')) {
      var id = parseInt(atc.getAttribute('data-variant-id'), 10);
      if (!id) return;

      // Gift card only. `false` = the buyer asked to send it to someone but the details are
      // wrong — abort BEFORE disabling the button so the error is the only thing that moves.
      var recipient = collectRecipient();
      if (recipient === false) return;

      var item = { id: id, quantity: 1 };
      if (recipient) item.properties = recipient;

      atc.disabled = true;
      postCart([item])
        .then(function (state) { renderCart(state); gcError(''); })
        .catch(function (err) {
          console.error('[hh-sp] add-to-cart', err);
          if (recipient) gcError(recipientErrorText(err && err.body));
        })
        .then(function () { atc.disabled = false; });
    }
  });

  initGiftRecipient();
  update();           // initial sync (no gallery nav)
  initialized = true; // subsequent variant changes may drive the gallery
})();
